// ── Swipe a bottom sheet down to close it ─────────────────────────────────────
// Jade, 2026-10-22: every sheet that slides up should also follow the finger
// down and close, like the sheets in other apps. Behaviour copied from the
// common libraries (vaul on the web, Material's BottomSheetBehavior on Android):
//   • the sheet follows the finger downward, and the dim fades with it;
//   • let go past CLOSE_FRACTION of the sheet's height, or with a downward flick
//     faster than FLING_PX_PER_MS, and it slides the rest of the way and closes;
//     otherwise it springs back;
//   • a drag only starts while whatever list the finger is on is scrolled to its
//     top, so scrolling a list inside the sheet still scrolls it;
//   • an upward or sideways move is left to the page (scroll / sliders), and so
//     is a touch on a reorder handle.
// Everything is written straight to the DOM (transform / opacity), so a drag
// costs no React render.
//
// Usage:
//   const sheet = useSheetDrag(onClose);
//   <div ref={sheet.dimRef} onClick={sheet.dismiss} .../>   // optional dim layer
//   <div ref={sheet.sheetRef} ...>...</div>                  // the sheet itself
// `dismiss()` slides the sheet down and then calls onClose (used for a tap on
// the dim too, so every way out looks the same). The dim must be its own layer,
// not the sheet's parent, or fading it would fade the sheet as well.
import { useCallback, useEffect, useRef } from "react";

export const CLOSE_FRACTION = 0.25;
export const FLING_PX_PER_MS = 0.4;
const SLOP_PX = 6;
const OUT_MS = 220;
const BACK_MS = 280;

// Pure decision, exported for the test: close or spring back?
export function shouldCloseSheet(dragPx, sheetHeight, velocityPxPerMs) {
  if (velocityPxPerMs > FLING_PX_PER_MS) return true;
  if (velocityPxPerMs < -FLING_PX_PER_MS) return false; // flicked back up
  return dragPx > Math.max(40, sheetHeight * CLOSE_FRACTION);
}

// The nearest element between `target` and `sheet` (inclusive) that scrolls vertically.
function scrollerOf(target, sheet) {
  for (let el = target; el && el !== sheet.parentNode; el = el.parentNode) {
    if (el.nodeType !== 1) continue;
    if (el.scrollHeight > el.clientHeight + 1) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === "auto" || oy === "scroll") return el;
    }
  }
  return null;
}

export function useSheetDrag(onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const sheetEl = useRef(null);
  const dimEl = useRef(null);
  const closing = useRef(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const slideOut = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    const sh = sheetEl.current, dim = dimEl.current;
    if (!sh) { onCloseRef.current && onCloseRef.current(); return; }
    // An entrance animation with fill "both" would hold the transform; drop it.
    sh.style.animation = "none";
    sh.style.transition = `transform ${OUT_MS}ms cubic-bezier(0.32,0.72,0,1)`;
    sh.style.transform = "translateY(100%)";
    if (dim) {
      dim.style.animation = "none";
      dim.style.transition = `opacity ${OUT_MS}ms ease`;
      dim.style.opacity = "0";
    }
    timer.current = setTimeout(() => { onCloseRef.current && onCloseRef.current(); }, OUT_MS - 10);
  }, []);

  // Ignores its argument, so it can be an onClick directly. Taps inside the
  // sheet must not reach it: every sheet stops propagation on itself.
  const dismiss = useCallback(() => slideOut(), [slideOut]);

  const attach = useCallback((sh) => {
    let startX = 0, startY = 0, dragging = false, decided = false, scroller = null;
    let samples = [];
    let height = 1;
    const dim = () => dimEl.current;

    const onStart = (e) => {
      if (closing.current || e.touches.length !== 1) { decided = true; dragging = false; return; }
      const t = e.touches[0];
      startX = t.clientX; startY = t.clientY;
      dragging = false; decided = false;
      const tag = e.target && e.target.tagName;
      if (tag === "INPUT" && e.target.type === "range") { decided = true; return; }
      // Something that drags on its own (a dnd-kit reorder handle, or anything
      // marked data-no-sheet-drag) keeps its gesture.
      if (e.target.closest && e.target.closest('[aria-roledescription="sortable"],[data-no-sheet-drag]')) { decided = true; return; }
      scroller = scrollerOf(e.target, sh);
      samples = [{ y: startY, t: performance.now() }];
    };
    const onMove = (e) => {
      if (decided && !dragging) return;
      const t = e.touches[0];
      const dx = t.clientX - startX, dy = t.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
        decided = true;
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || (scroller && scroller.scrollTop > 0)) return;
        dragging = true;
        startY = t.clientY; // start from here so the sheet doesn't jump by the slop
        height = sh.getBoundingClientRect().height || 1;
        sh.style.animation = "none";
        sh.style.transition = "none";
        const d = dim();
        if (d) { d.style.animation = "none"; d.style.transition = "none"; }
      }
      if (!dragging) return;
      if (e.cancelable) e.preventDefault(); // keep the page / list from scrolling too
      const raw = t.clientY - startY;
      // Above the start: a little give, like a rubber band, not a free pull up.
      const y = raw >= 0 ? raw : -Math.min(24, Math.sqrt(-raw) * 2);
      sh.style.transform = `translateY(${y}px)`;
      const d = dim();
      if (d) d.style.opacity = String(Math.max(0, 1 - Math.max(0, y) / height));
      samples.push({ y: t.clientY, t: performance.now() });
      if (samples.length > 6) samples.shift();
    };
    const onEnd = (e) => {
      if (!dragging) return;
      dragging = false;
      const last = samples[samples.length - 1];
      // Speed over the last ~100ms of the move.
      let first = samples[0];
      for (const s of samples) if (last.t - s.t <= 100) { first = s; break; }
      const v = last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0;
      const y = last.y - startY;
      if (shouldCloseSheet(y, height, v)) { slideOut(); return; }
      sh.style.transition = `transform ${BACK_MS}ms cubic-bezier(0.22,1,0.36,1)`;
      sh.style.transform = "translateY(0)";
      const d = dim();
      if (d) { d.style.transition = `opacity ${BACK_MS}ms ease`; d.style.opacity = "1"; }
      if (e && e.cancelable) e.preventDefault(); // no click on whatever the finger lifted over
    };

    sh.addEventListener("touchstart", onStart, { passive: true });
    sh.addEventListener("touchmove", onMove, { passive: false });
    sh.addEventListener("touchend", onEnd);
    sh.addEventListener("touchcancel", onEnd);
    return () => {
      sh.removeEventListener("touchstart", onStart);
      sh.removeEventListener("touchmove", onMove);
      sh.removeEventListener("touchend", onEnd);
      sh.removeEventListener("touchcancel", onEnd);
    };
  }, [slideOut]);

  // Pass sheetRef / dimRef as refs directly (they're stable), or from a
  // useCallback: a fresh inline ref function would re-attach on every render.
  const detach = useRef(null);
  const sheetRef = useCallback((el) => {
    if (el && el === sheetEl.current) return;
    if (detach.current) { detach.current(); detach.current = null; }
    if (el) { clearTimeout(timer.current); closing.current = false; detach.current = attach(el); }
    sheetEl.current = el;
  }, [attach]);
  const dimRef = useCallback((el) => { dimEl.current = el; }, []);

  return { sheetRef, dimRef, dismiss };
}
